<script setup>
/* global axios */
// [AstraChat] Pop-up do compositor para ENVIAR mensagens ricas do WhatsApp pela
// caixa AstraCall: produto, contato, Pix, enquete, evento — e votar numa enquete
// recebida. Chama /kanban/connections/send_rich (cria a bolha no timeline e o
// AstraCall entrega o conteúdo nativo). Só aparece em caixas AstraCall (gate no
// ReplyBottomPanel). Voto reusa /poll_vote.
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['close']);

const { t } = useI18n();
const route = useRoute();
const currentChat = useMapGetter('getSelectedChat');
const getInbox = useMapGetter('inboxes/getInbox');
// flags "rich_send" da conexão desta caixa (produto/contato/pix/enquete/evento).
// Ausente = LIGADO. Configurável em Configurações da conexão (provedor AstraCall).
// Começa com o valor do store (instantâneo) e, ao abrir, BUSCA o estado FRESCO do
// backend (by_inbox) — assim o menu sempre reflete o que está salvo, sem depender
// do cache do store (que pode estar velho após um reload).
const richFlags = ref(
  getInbox.value?.(currentChat.value?.inbox_id)?.kanban_rich_send || {}
);
const loadRichFlags = async () => {
  const inboxId = currentChat.value?.inbox_id;
  if (!inboxId) return;
  try {
    const { data } = await axios.get(`${base.value}/by_inbox`, {
      params: { inbox_id: inboxId },
    });
    if (data?.data?.meta?.rich_send) richFlags.value = data.data.meta.rich_send;
  } catch (e) {
    // mantém o fallback do store
  }
};
const base = computed(
  () => `/api/v1/accounts/${route.params.accountId}/kanban/connections`
);
// chaves Pix ficam em /kanban/pix_keys (fora do namespace connections)
const pixKeysBase = computed(
  () => `/api/v1/accounts/${route.params.accountId}/kanban/pix_keys`
);
const convId = computed(() => currentChat.value?.id);

const view = ref('menu');
const sending = ref(false);
const error = ref('');

const product = ref({
  title: '',
  price: '',
  sale_price: '',
  currency: 'R$',
  description: '',
  url: '',
  image_url: '',
});
const pix = ref({
  key: '',
  amount: '',
  merchant_name: '',
  merchant_city: '',
  txid: '',
  description: '',
});

// ---- Contatos (busca no Chatwoot + vários + manual) ----
const contactQuery = ref('');
const contactResults = ref([]);
const selectedContacts = ref([]);
const searchingContacts = ref(false);
const showManualContact = ref(false);
const manualContact = ref({ full_name: '', phone: '' });
let searchTimer = null;
const searchContacts = () => {
  clearTimeout(searchTimer);
  const q = contactQuery.value.trim();
  if (!q) {
    contactResults.value = [];
    return;
  }
  searchTimer = setTimeout(async () => {
    searchingContacts.value = true;
    try {
      const { data } = await axios.get(
        `/api/v1/accounts/${route.params.accountId}/contacts/search`,
        { params: { q } }
      );
      contactResults.value = (data?.payload || [])
        .map(c => ({ id: c.id, full_name: c.name, phone: c.phone_number }))
        .filter(c => c.phone);
    } catch {
      contactResults.value = [];
    } finally {
      searchingContacts.value = false;
    }
  }, 300);
};
const pickContact = c => {
  if (!selectedContacts.value.some(x => x.phone === c.phone)) {
    selectedContacts.value.push({ full_name: c.full_name, phone: c.phone });
  }
  contactQuery.value = '';
  contactResults.value = [];
};
const addManualContact = () => {
  const c = manualContact.value;
  if (!c.full_name.trim() || !c.phone.trim()) return;
  selectedContacts.value.push({
    full_name: c.full_name.trim(),
    phone: c.phone.trim(),
  });
  manualContact.value = { full_name: '', phone: '' };
  showManualContact.value = false;
};
const removeContact = i => selectedContacts.value.splice(i, 1);

// ---- Chaves Pix salvas ----
const pixKeys = ref([]);
const selectedPixKeyId = ref('');
const loadPixKeys = async () => {
  try {
    const { data } = await axios.get(pixKeysBase.value);
    pixKeys.value = data?.data || [];
  } catch {
    pixKeys.value = [];
  }
};
const applyPixKey = () => {
  const k = pixKeys.value.find(x => String(x.id) === String(selectedPixKeyId.value));
  if (!k) return;
  pix.value.key = k.pix_key || '';
  pix.value.merchant_name = k.merchant_name || '';
  pix.value.merchant_city = k.merchant_city || '';
};
onMounted(loadPixKeys);
onMounted(loadRichFlags);
const poll = ref({ name: '', options: ['', ''], multiple_answers: false });
const event = ref({
  name: '',
  description: '',
  location: '',
  join_link: '',
  date: '',
  time: '',
});

// rótulo/descrição vêm do i18n (ASTRACALL_RICH.types.<key>) para seguir o idioma do usuário.
const TYPES = [
  { key: 'product', icon: 'i-lucide-shopping-bag' },
  { key: 'contact', icon: 'i-lucide-user-round' },
  { key: 'pix', icon: 'i-lucide-qr-code' },
  { key: 'poll', icon: 'i-lucide-bar-chart-3' },
  { key: 'event', icon: 'i-lucide-calendar-days' },
  { key: 'vote', icon: 'i-lucide-vote' },
].map(ty => ({
  ...ty,
  get label() {
    return t(`ASTRACALL_RICH.types.${ty.key}.label`);
  },
  get desc() {
    return t(`ASTRACALL_RICH.types.${ty.key}.desc`);
  },
}));

// tipos habilitados nesta conexão. 'vote' (responder enquete recebida) segue o
// flag de 'poll'. Chave ausente = habilitada.
const enabledTypes = computed(() =>
  TYPES.filter(ty => {
    const key = ty.key === 'vote' ? 'poll' : ty.key;
    return richFlags.value[key] !== false;
  })
);

const titleFor = computed(
  () => TYPES.find(ty => ty.key === view.value)?.label ||
    t('ASTRACALL_RICH.fallbackTitle')
);

// enquetes recebidas na conversa (para o fluxo de voto)
const receivedPolls = computed(() =>
  (currentChat.value?.messages || [])
    .filter(m => typeof m.content === 'string' && /^\s*📊/.test(m.content))
    .map(m => ({
      id: (m.content.match(/_PID:\s*([^_]+)_/) || [])[1]?.trim() || m.source_id,
      question: (m.content.match(/📊\s*\*([^*]+)\*/) || [])[1]?.trim() ||
        t('ASTRACALL_RICH.poll.defaultQuestion'),
      options: m.content
        .split('\n')
        .map(l => (l.match(/^\s*\d+[.)]\s*(.+)$/) || [])[1])
        .filter(Boolean),
    }))
    .filter(p => p.id)
);
const votePoll = ref(null);

const goBack = () => {
  error.value = '';
  if (view.value === 'vote' && votePoll.value) {
    votePoll.value = null;
    return;
  }
  view.value = 'menu';
};

const post = async (path, body, okMsg) => {
  if (!convId.value) {
    error.value = t('ASTRACALL_RICH.errors.noConversation');
    return;
  }
  sending.value = true;
  error.value = '';
  try {
    await axios.post(`${base.value}/${path}`, {
      conversation_id: convId.value,
      ...body,
    });
    useAlert(okMsg);
    emit('close');
  } catch (e) {
    error.value =
      e?.response?.data?.error || t('ASTRACALL_RICH.errors.sendFailed');
  } finally {
    sending.value = false;
  }
};

const sendRich = (kind, payload, okMsg) =>
  post('send_rich', { kind, ...payload }, okMsg);

const submitProduct = () =>
  sendRich('product', product.value, t('ASTRACALL_RICH.success.product'));
const submitContact = () => {
  if (!selectedContacts.value.length) {
    error.value = t('ASTRACALL_RICH.errors.contactRequired');
    return;
  }
  sendRich(
    'contact',
    { contacts: selectedContacts.value },
    selectedContacts.value.length > 1
      ? t('ASTRACALL_RICH.success.contacts')
      : t('ASTRACALL_RICH.success.contact')
  );
};
const submitPix = () =>
  sendRich('pix', pix.value, t('ASTRACALL_RICH.success.pix'));
const submitPoll = () =>
  sendRich(
    'poll',
    { ...poll.value, options: poll.value.options.filter(o => o.trim()) },
    t('ASTRACALL_RICH.success.poll')
  );
const submitEvent = () => {
  if (!event.value.date) {
    error.value = t('ASTRACALL_RICH.errors.eventDateRequired');
    return;
  }
  const p = { ...event.value };
  const dt = new Date(`${event.value.date}T${event.value.time || '09:00'}`);
  if (!Number.isNaN(dt.getTime())) p.start_time = Math.floor(dt.getTime() / 1000);
  sendRich('event', p, t('ASTRACALL_RICH.success.event'));
};
const submitVote = optName =>
  post(
    'poll_vote',
    { poll_message_id: votePoll.value.id, selected_names: [optName] },
    t('ASTRACALL_RICH.success.vote')
  );

const addOption = () => poll.value.options.push('');
const removeOption = i => {
  if (poll.value.options.length > 2) poll.value.options.splice(i, 1);
};
</script>

<template>
  <teleport to="body">
    <div
      class="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4"
      @click.self="emit('close')"
    >
      <div
        class="w-full max-w-md rounded-xl bg-n-solid-1 border border-n-weak shadow-xl flex flex-col max-h-[85vh]"
      >
        <!-- header -->
        <div class="flex items-center gap-2 px-4 py-3 border-b border-n-weak">
          <button
            v-if="view !== 'menu'"
            type="button"
            class="grid place-items-center size-7 rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
            @click="goBack"
          >
            <span class="i-lucide-arrow-left size-4" />
          </button>
          <h3 class="text-heading-3 text-n-slate-12 flex-1">
            {{ view === 'menu' ? t('ASTRACALL_RICH.title') : titleFor }}
          </h3>
          <button
            type="button"
            class="grid place-items-center size-7 rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
            @click="emit('close')"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <div class="p-4 overflow-y-auto">
          <p
            v-if="error"
            class="mb-3 text-sm text-n-ruby-11 bg-n-ruby-2 rounded-lg px-3 py-2"
          >
            {{ error }}
          </p>

          <!-- MENU -->
          <div v-if="view === 'menu'" class="grid grid-cols-2 gap-2">
            <button
              v-for="ty in enabledTypes"
              :key="ty.key"
              type="button"
              class="flex flex-col items-start gap-1 p-3 rounded-lg border border-n-weak hover:bg-n-alpha-2 text-left"
              @click="view = ty.key"
            >
              <span :class="ty.icon" class="size-5 text-n-brand" />
              <span class="text-sm font-medium text-n-slate-12">{{ ty.label }}</span>
              <span class="text-xs text-n-slate-11">{{ ty.desc }}</span>
            </button>
          </div>

          <!-- PRODUTO -->
          <form
            v-else-if="view === 'product'"
            class="flex flex-col gap-3"
            @submit.prevent="submitProduct"
          >
            <label class="text-sm">
              <span class="text-n-slate-11">{{ t('ASTRACALL_RICH.product.title') }}</span>
              <input v-model="product.title" class="rich-input" :placeholder="t('ASTRACALL_RICH.product.titlePlaceholder')" />
            </label>
            <div class="grid grid-cols-3 gap-2">
              <label class="text-sm col-span-1">
                <span class="text-n-slate-11">{{ t('ASTRACALL_RICH.product.currency') }}</span>
                <input v-model="product.currency" class="rich-input" placeholder="R$" />
              </label>
              <label class="text-sm col-span-1">
                <span class="text-n-slate-11">{{ t('ASTRACALL_RICH.product.price') }}</span>
                <input v-model="product.price" class="rich-input" placeholder="79.90" />
              </label>
              <label class="text-sm col-span-1">
                <span class="text-n-slate-11">{{ t('ASTRACALL_RICH.product.salePrice') }}</span>
                <input v-model="product.sale_price" class="rich-input" placeholder="59.90" />
              </label>
            </div>
            <label class="text-sm">
              <span class="text-n-slate-11">{{ t('ASTRACALL_RICH.product.description') }}</span>
              <textarea v-model="product.description" rows="2" class="rich-input" />
            </label>
            <label class="text-sm">
              <span class="text-n-slate-11">{{ t('ASTRACALL_RICH.product.url') }}</span>
              <input v-model="product.url" class="rich-input" placeholder="https://..." />
            </label>
            <label class="text-sm">
              <span class="text-n-slate-11">{{ t('ASTRACALL_RICH.product.image') }}</span>
              <input v-model="product.image_url" class="rich-input" placeholder="https://.../foto.jpg" />
            </label>
            <NextButton type="submit" :label="t('ASTRACALL_RICH.product.submit')" :is-loading="sending" class="self-end" />
          </form>

          <!-- CONTATO -->
          <form
            v-else-if="view === 'contact'"
            class="flex flex-col gap-3"
            @submit.prevent="submitContact"
          >
            <div v-if="selectedContacts.length" class="flex flex-col gap-1.5">
              <div
                v-for="(c, i) in selectedContacts"
                :key="i"
                class="flex items-center gap-2 px-3 py-2 rounded-lg bg-n-alpha-2"
              >
                <span class="i-lucide-user-round size-4 text-n-slate-10 shrink-0" />
                <span class="text-sm text-n-slate-12 flex-1 truncate">
                  {{ c.full_name }} · {{ c.phone }}
                </span>
                <button
                  type="button"
                  class="text-n-slate-10 hover:text-n-ruby-11"
                  @click="removeContact(i)"
                >
                  <span class="i-lucide-x size-4" />
                </button>
              </div>
            </div>

            <div class="relative">
              <input
                v-model="contactQuery"
                class="rich-input"
                :placeholder="t('ASTRACALL_RICH.contact.searchPlaceholder')"
                @input="searchContacts"
              />
              <div
                v-if="contactResults.length"
                class="absolute z-10 left-0 right-0 mt-1 max-h-48 overflow-y-auto rounded-lg border border-n-weak bg-n-solid-1 shadow-lg"
              >
                <button
                  v-for="c in contactResults"
                  :key="c.id"
                  type="button"
                  class="flex items-center gap-2 w-full px-3 py-2 text-left text-sm hover:bg-n-alpha-2"
                  @click="pickContact(c)"
                >
                  <span class="text-n-slate-12 truncate">{{ c.full_name || t('ASTRACALL_RICH.contact.noName') }}</span>
                  <span class="text-n-slate-10 ms-auto shrink-0">{{ c.phone }}</span>
                </button>
              </div>
              <span
                v-else-if="searchingContacts"
                class="absolute right-3 top-2.5 text-xs text-n-slate-10"
              >
                {{ t('ASTRACALL_RICH.contact.searching') }}
              </span>
            </div>

            <button
              v-if="!showManualContact"
              type="button"
              class="self-start text-sm text-n-brand hover:underline"
              @click="showManualContact = true"
            >
              {{ t('ASTRACALL_RICH.contact.addManual') }}
            </button>
            <div v-else class="flex flex-col gap-2 p-3 rounded-lg border border-n-weak">
              <input v-model="manualContact.full_name" class="rich-input !mt-0" :placeholder="t('ASTRACALL_RICH.contact.namePlaceholder')" />
              <input v-model="manualContact.phone" class="rich-input !mt-0" :placeholder="t('ASTRACALL_RICH.contact.phonePlaceholder')" />
              <div class="flex gap-3 justify-end text-sm">
                <button type="button" class="text-n-slate-11" @click="showManualContact = false">
                  {{ t('ASTRACALL_RICH.contact.cancel') }}
                </button>
                <button type="button" class="text-n-brand font-medium" @click="addManualContact">
                  {{ t('ASTRACALL_RICH.contact.add') }}
                </button>
              </div>
            </div>

            <NextButton
              type="submit"
              :label="t('ASTRACALL_RICH.contact.submit')"
              :is-loading="sending"
              class="self-end"
            />
          </form>

          <!-- PIX -->
          <form
            v-else-if="view === 'pix'"
            class="flex flex-col gap-3"
            @submit.prevent="submitPix"
          >
            <label v-if="pixKeys.length" class="text-sm">
              <span class="text-n-slate-11">{{ t('ASTRACALL_RICH.pix.useSaved') }}</span>
              <select
                v-model="selectedPixKeyId"
                class="rich-input"
                @change="applyPixKey"
              >
                <option value="">{{ t('ASTRACALL_RICH.pix.newManual') }}</option>
                <option v-for="k in pixKeys" :key="k.id" :value="k.id">
                  {{ k.label || k.pix_key }}
                </option>
              </select>
            </label>
            <label class="text-sm">
              <span class="text-n-slate-11">{{ t('ASTRACALL_RICH.pix.key') }}</span>
              <input v-model="pix.key" class="rich-input" :placeholder="t('ASTRACALL_RICH.pix.keyPlaceholder')" />
            </label>
            <label class="text-sm">
              <span class="text-n-slate-11">{{ t('ASTRACALL_RICH.pix.amount') }}</span>
              <input v-model="pix.amount" class="rich-input" placeholder="49.90" />
            </label>
            <div class="grid grid-cols-2 gap-2">
              <label class="text-sm">
                <span class="text-n-slate-11">{{ t('ASTRACALL_RICH.pix.merchantName') }}</span>
                <input v-model="pix.merchant_name" class="rich-input" :placeholder="t('ASTRACALL_RICH.pix.merchantPlaceholder')" />
              </label>
              <label class="text-sm">
                <span class="text-n-slate-11">{{ t('ASTRACALL_RICH.pix.city') }}</span>
                <input v-model="pix.merchant_city" class="rich-input" :placeholder="t('ASTRACALL_RICH.pix.cityPlaceholder')" />
              </label>
            </div>
            <label class="text-sm">
              <span class="text-n-slate-11">{{ t('ASTRACALL_RICH.pix.description') }}</span>
              <input v-model="pix.description" class="rich-input" :placeholder="t('ASTRACALL_RICH.pix.descriptionPlaceholder')" />
            </label>
            <NextButton type="submit" :label="t('ASTRACALL_RICH.pix.submit')" :is-loading="sending" class="self-end" />
          </form>

          <!-- ENQUETE -->
          <form
            v-else-if="view === 'poll'"
            class="flex flex-col gap-3"
            @submit.prevent="submitPoll"
          >
            <label class="text-sm">
              <span class="text-n-slate-11">{{ t('ASTRACALL_RICH.poll.question') }}</span>
              <input v-model="poll.name" class="rich-input" :placeholder="t('ASTRACALL_RICH.poll.questionPlaceholder')" />
            </label>
            <div class="flex flex-col gap-2">
              <span class="text-n-slate-11 text-sm">{{ t('ASTRACALL_RICH.poll.options') }}</span>
              <div v-for="(o, i) in poll.options" :key="i" class="flex items-center gap-2">
                <input v-model="poll.options[i]" class="rich-input" :placeholder="t('ASTRACALL_RICH.poll.optionPlaceholder', { n: i + 1 })" />
                <button
                  v-if="poll.options.length > 2"
                  type="button"
                  class="grid place-items-center size-8 rounded-lg text-n-slate-11 hover:bg-n-alpha-2 shrink-0"
                  @click="removeOption(i)"
                >
                  <span class="i-lucide-trash-2 size-4" />
                </button>
              </div>
              <button
                type="button"
                class="self-start text-sm text-n-brand hover:underline"
                @click="addOption"
              >
                {{ t('ASTRACALL_RICH.poll.addOption') }}
              </button>
            </div>
            <label class="flex items-center gap-2 text-sm text-n-slate-12">
              <input v-model="poll.multiple_answers" type="checkbox" />
              {{ t('ASTRACALL_RICH.poll.multiple') }}
            </label>
            <NextButton type="submit" :label="t('ASTRACALL_RICH.poll.submit')" :is-loading="sending" class="self-end" />
          </form>

          <!-- EVENTO -->
          <form
            v-else-if="view === 'event'"
            class="flex flex-col gap-3"
            @submit.prevent="submitEvent"
          >
            <label class="text-sm">
              <span class="text-n-slate-11">{{ t('ASTRACALL_RICH.event.name') }}</span>
              <input v-model="event.name" class="rich-input" :placeholder="t('ASTRACALL_RICH.event.namePlaceholder')" />
            </label>
            <div class="grid grid-cols-2 gap-2">
              <label class="text-sm">
                <span class="text-n-slate-11">{{ t('ASTRACALL_RICH.event.date') }}</span>
                <input v-model="event.date" type="date" class="rich-input" />
              </label>
              <label class="text-sm">
                <span class="text-n-slate-11">{{ t('ASTRACALL_RICH.event.time') }}</span>
                <input v-model="event.time" type="time" class="rich-input" />
              </label>
            </div>
            <label class="text-sm">
              <span class="text-n-slate-11">{{ t('ASTRACALL_RICH.event.location') }}</span>
              <input v-model="event.location" class="rich-input" :placeholder="t('ASTRACALL_RICH.event.locationPlaceholder')" />
            </label>
            <label class="text-sm">
              <span class="text-n-slate-11">{{ t('ASTRACALL_RICH.event.link') }}</span>
              <input v-model="event.join_link" class="rich-input" placeholder="https://meet.google.com/..." />
            </label>
            <label class="text-sm">
              <span class="text-n-slate-11">{{ t('ASTRACALL_RICH.event.description') }}</span>
              <textarea v-model="event.description" rows="2" class="rich-input" />
            </label>
            <NextButton type="submit" :label="t('ASTRACALL_RICH.event.submit')" :is-loading="sending" class="self-end" />
          </form>

          <!-- VOTO -->
          <div v-else-if="view === 'vote'" class="flex flex-col gap-3">
            <template v-if="!votePoll">
              <p v-if="!receivedPolls.length" class="text-sm text-n-slate-11">
                {{ t('ASTRACALL_RICH.vote.none') }}
              </p>
              <button
                v-for="(p, i) in receivedPolls"
                :key="i"
                type="button"
                class="flex items-center gap-2 p-3 rounded-lg border border-n-weak hover:bg-n-alpha-2 text-left"
                @click="votePoll = p"
              >
                <span class="i-lucide-bar-chart-3 size-4 text-n-brand shrink-0" />
                <span class="text-sm text-n-slate-12">{{ p.question }}</span>
              </button>
            </template>
            <template v-else>
              <p class="text-sm font-medium text-n-slate-12">{{ votePoll.question }}</p>
              <button
                v-for="(opt, i) in votePoll.options"
                :key="i"
                type="button"
                class="p-2.5 rounded-lg border border-n-weak hover:bg-n-alpha-2 text-left text-sm text-n-slate-12 disabled:opacity-60"
                :disabled="sending"
                @click="submitVote(opt)"
              >
                {{ opt }}
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<style scoped>
.rich-input {
  @apply block w-full mt-1 px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-1 text-sm text-n-slate-12 outline-none focus:border-n-brand;
}
</style>
